import React, {
  useState, useCallback, useEffect, useMemo, useRef, forwardRef,
} from 'react';
import { CSVLink, CSVDownload } from 'react-csv';
import { toast } from 'react-toastify';
import DatePicker from 'react-date-picker';
import AvTimerIcon from '@material-ui/icons/AvTimer';
import { first, forEach } from 'lodash';
import { collapseTextChangeRangesAcrossMultipleVersions } from 'typescript';
import PriceBox from '../WXPay/PriceBox';
import CollapseBox from './CollapseBox';
import Input from '../DesignSystem/input';
import Button from '../DesignSystem/button';
import SearchIcon from '../../assets/images/search-icon.svg';
import CalenderIcon from '../../assets/images/calender-icon.svg';
import FeesTable from '../WXPay/Fees';
import PortsTable from '../WXPay/PortsTable';
import BandwidthsTable from '../WXPay/BandwidthsTable';
import { SortOrder } from '../../enums';
import WXPayService from '../../services/wxPay.service';
import { csvPriceRowType, priceRowType, TableConstants } from '../../utils/appConstants';
import { currencyFormatter } from '../../utils/methods';
import PriceTable from './PriceTable';
import Dropdown from '../DesignSystem/dropdown_autocomplete';
import {
  SuccessMessage, ErrorCodeMessageMapping, ErrorCodesMapping, MY_SELLER_BUCKET_PRE_URL, TERMS_DEFAULT_FILE, ErrorMessages as Errors,
} from '../../utils/apiConstants';
import ErrorInfoIcon from '../../assets/images/error-info-icon.svg';
import { useCSVData, useDropDownSet } from '../../utils/customHooks';
import CSVComponent from './CSVComponent';

const ReportsComponent: React.FC = () => {
  const [activeStartDateStart, setActiveStartDateStart] = useState<Date>();
  const [activeStartDateEnd, setActiveStartDateEnd] = useState<Date>();
  const { csvData, setCSVData } = useCSVData();
  const { array: organizations, addSet: addOrganizations } = useDropDownSet();
  const [orgInput, setOrgInput] = useState<string>();
  const [clearInput, setClearInput] = useState<boolean>(false);
  type filterType = { startDate: Date, endDate: Date, organization: string, searchText: string };
  const firstLoadFilters: filterType = useMemo(() => {
    return {
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).setHours(23, 59, 59, 999)),
      organization: '',
      searchText: '',
    };
  }, []);
  const [filters, setFilters] = useState<filterType>(firstLoadFilters);
  const [previousFilters, setPreviousFilters] = useState<filterType>(firstLoadFilters);
  const [collapsibleStates, setCollapsibleStates] = useState<{
      income: boolean, expense: boolean
    }>({
      expense: false,
      income: false,
    });
  const filtersEqual = useCallback((filters1: filterType, filters2: filterType): boolean => {
    return filters1.endDate.valueOf() === filters2.endDate.valueOf()
    && filters1.startDate.valueOf() === filters2.startDate.valueOf()
    && filters1.organization === filters2.organization
    && filters1.searchText === filters2.searchText;
  }, []);
  const handleOrganizationChange = useCallback((organization: string) => {
    setFilters((prev: any) => {
      return {
        ...prev,
        organization,
      };
    });
  }, [setFilters]);
  const isComponentMounted = React.useRef(true);
  React.useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
    };
  }, []);
  const onDateChange = useCallback((isStartDate: boolean, e: any) => {
    const beginOfMonth = new Date(e.getFullYear(), e.getMonth(), 1);
    if (isStartDate) {
      setActiveStartDateStart(beginOfMonth);
      setFilters((prev: any) => {
        return {
          ...prev,
          startDate: new Date(e),
        };
      });
    } else {
      setActiveStartDateEnd(beginOfMonth);
      setFilters((prev: any) => {
        return {
          ...prev,
          endDate: new Date(new Date(e).setHours(23, 59, 59, 999)),
        };
      });
    }
  }, [setFilters]);

  const handleCollapsibleNEClose = useCallback((id: string) => {
    setCollapsibleStates((prev: any) => {
      return {
        ...prev,
        [id]: false,
      };
    });
  }, [setCollapsibleStates]);

  const handleCollapsibleNEOpen = useCallback((id: string) => {
    setCollapsibleStates((prev: any) => {
      return {
        ...prev,
        [id]: true,
      };
    });
  }, [setCollapsibleStates]);

  const handleSearchChange = useCallback((event) => {
    setFilters((prev) => {
      return {
        ...prev,
        searchText: event.target.value.trim(),
      };
    });
  }, []);

  const timeFormat = useCallback(
    (date : any) => {
      let hours = date.getHours();
      let minutes = date.getMinutes();
      let seconds = date.getSeconds();
      const am_pm = hours >= 12 ? 'PM' : 'AM';
      hours = (hours % 12) || 12;
      hours = hours < 10 ? `0${hours}` : hours;
      minutes = minutes < 10 ? `0${minutes}` : minutes;
      seconds = seconds < 10 ? `0${seconds}` : seconds;
      const strTime = `${hours}:${minutes}:${seconds} ${am_pm}`;
      return strTime;
    },
    [],
  );
  const nowTimeHandler = useCallback(
    () => {
      setFilters((prev: any) => {
        const date = new Date();
        const beginOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
        setActiveStartDateEnd(beginOfMonth);
        if (new Date(prev.startDate) > new Date(Date.now())) {
          setActiveStartDateStart(beginOfMonth);
          setActiveStartDateEnd(beginOfMonth);
          return {
            ...prev,
            startDate: new Date(new Date().setHours(0, 0, 0, 0)),
            endDate: new Date(Date.now()),
          };
        }
        return {
          ...prev,
          endDate: new Date(Date.now()),
        };
      });
    },
    [setFilters],
  );

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<priceRowType[]>();
  const [totals, setTotals] = useState<{expense: number, income: number}>({ expense: 0, income: 0 });
  const [selectedDateRangeTotal, setSelectedDateRangeTotal] = useState(0);
  const getIncomeExpense = useCallback(
    (filter?: any) => {
      setLoading(true);
      const useFilters = (filter) || filters;
      setPreviousFilters(useFilters);
      const startDate = Date.parse(new Date(useFilters.startDate).toUTCString());
      const endDate = Date.parse(new Date(useFilters.endDate).toUTCString());
      const payload = {
        startDate,
        endDate,
        organization: useFilters.organization,
        searchText: useFilters.searchText,
      };
      WXPayService.getWXPayReport(payload).then((response: any) => {
        if (!isComponentMounted.current) return;
        setRows((response?.data?.priceRows?.income || []).concat(response?.data?.priceRows?.expense || []));
        setSelectedDateRangeTotal(response?.data?.selectedDateRangeTotal);
        setCSVData(
          response?.data?.priceRows.income,
          response?.data?.priceRows.expense,
          { start: useFilters.startDate, end: useFilters.endDate },
        );
        setTotals(response?.data?.totals);
        addOrganizations(response?.data?.organizations);
        setLoading(false);
      })
        .catch((e:any) => {
          if (!isComponentMounted.current) return;
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          setLoading(false);
        });
    },
    [filters, setLoading, setSelectedDateRangeTotal, setRows, setTotals, setCSVData, addOrganizations],
  );
  useEffect(() => {
    getIncomeExpense();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = useCallback(() => {
    getIncomeExpense(firstLoadFilters);
    setFilters(firstLoadFilters);
    setPreviousFilters(firstLoadFilters);
    setClearInput((prev) => !prev);
  }, [firstLoadFilters, getIncomeExpense]);

  const table = useMemo(() => {
    return <PriceTable priceRow={rows || []} loading={loading} />;
  }, [rows, loading]);

  const csvButton = useMemo(() => {
    return {
      income: (<CSVComponent
        type="income"
        data={csvData?.income || []}
        disabled={loading}
      />),
      expense: (<CSVComponent
        type="expense"
        data={csvData?.expense || []}
        disabled={loading}
      />),
    };
  }, [loading, csvData]);

  return (
    <div className="wx-pay-component-root">
      <div className="filter-boxes">
        <div>
          <div className="date-root">
            <label>Start Date </label>
            <DatePicker
              activeStartDate={activeStartDateStart}
              onActiveStartDateChange={(e) => setActiveStartDateStart(e.activeStartDate)}
              onChange={(date: any) => onDateChange(true, date)}
              value={filters.startDate}
              format="MM/dd/yyyy"
              showLeadingZeros
              calendarIcon={<img src={CalenderIcon} alt="Select Date" />}
              clearIcon={null}
              maxDate={filters.endDate}
            />
            <span className="time">{timeFormat(filters.startDate)}</span>
          </div>
          <div className="date-root">
            <label>
              End Date
              <AvTimerIcon className="now-icon" onClick={nowTimeHandler} />
            </label>
            <DatePicker
              activeStartDate={activeStartDateEnd}
              onActiveStartDateChange={(e) => setActiveStartDateEnd(e.activeStartDate)}
              onChange={(date: any) => onDateChange(false, date)}
              value={filters.endDate}
              format="MM/dd/yyyy"
              showLeadingZeros
              calendarIcon={<img src={CalenderIcon} alt="Select Date" />}
              clearIcon={null}
              minDate={filters.startDate}
            />
            <span className="time">{timeFormat(filters.endDate)}</span>
          </div>
          <div className="filter-flex">
            <div className="dropdown-box">
              <label>Organization</label>
              <Dropdown
                className="wxpay wxpaydropdown"
                inputValue={orgInput || ''}
                onInputChange={setOrgInput}
                value={filters.organization || ''}
                options={organizations || []}
                onChange={handleOrganizationChange}
              />
            </div>
          </div>
          {/* TODO: Issue with backend search text w/ new API */}
          {/* <div className="filter-flex">
            <div className="search-box">
              <div className="search-input-div">
                <label>Search</label>
                <Input clearInput={clearInput} variant="input-pre-icon" name="user-search" placeholder="" icon={SearchIcon} onChange={handleSearchChange} />
              </div>
            </div>
          </div> */}
        </div>
        <div>
          <div className="vertical-line" />
          <PriceBox loading={loading} text="Selected Date Range Total" amount={selectedDateRangeTotal < 0 ? `(${currencyFormatter(Math.abs(selectedDateRangeTotal))})` : currencyFormatter(Number(selectedDateRangeTotal))} color="#304052" />
        </div>
      </div>

      <div className="report-button-root">
        <div className="apply-filter-button">
          <Button
            variant="wxpay"
            node="Apply Filters"
            type="button"
            disabled={loading || filtersEqual(filters, previousFilters)}
            onClick={() => getIncomeExpense()}
            className="button-filter"
          />
          <Button
            variant="close"
            node="Reset Filters"
            type="button"
            disabled={loading || filtersEqual(previousFilters, firstLoadFilters)}
            onClick={() => resetFilters()}
            className="button-filter"
          />
        </div>
        <div className="get-invoice-button">
          <div>
            {csvButton.income}
          </div>

          <div>
            {csvButton.expense}
          </div>
        </div>
      </div>

      <div className="accordians">
        <CollapseBox
          text="Income"
          id="income"
          open={collapsibleStates.income}
          onCollapsibleNEClose={handleCollapsibleNEClose}
          onCollapsibleNEOpen={handleCollapsibleNEOpen}
          table={table}
          sums={{
            mrc: 0,
            total: (!loading) ? totals.income : 0,
          }}
          csvDownload={null}
        />
      </div>
    </div>
  );
};

export default ReportsComponent;
